import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAgentChat } from '../hooks/useAgentChat';
import useAgentChatStore from '../../../stores/agentChatStore';
import { CloseButton } from '@/components/ui';
import ToolResultCard from './ToolResultCard';
import DraftReviewPanel from './DraftReviewPanel';
import MarkdownText from '@/components/shared/MarkdownText';

import ConversationsPanelContent from '../../messaging/components/ConversationsPanelContent';
import EmailPanel from '../../messaging/components/EmailPanel';
import { ATTACHMENT_UPLOAD_ACCEPT, IMAGE_UPLOAD_ACCEPT, isSupportedImageFile } from '@/services/photoStorageService';

const MAX_FILES = 4;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ATTACHMENT_UPLOAD_ACCEPT;

/**
 * Slide-out chat panel with AI + People tabs.
 * 400px on desktop, full-width on mobile.
 */
export default function AgentChat() {
  const {
    isOpen,
    isStreaming,
    messages,
    error,
    close,
    sendMessage,
    cancel,
    clearMessages,
  } = useAgentChat();

  const { activeTab, setActiveTab, conversationUnreadCount } = useAgentChatStore();

  const [input, setInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const attachMenuRef = useRef(null);
  const dragCounterRef = useRef(0);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (activeTab === 'ai') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeTab]);

  // Focus input when panel opens on AI tab
  useEffect(() => {
    if (isOpen && activeTab === 'ai') {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen, activeTab]);

  // Close attach menu on outside click
  useEffect(() => {
    if (!showAttachMenu) return;
    const handler = (e) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) {
        setShowAttachMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAttachMenu]);

  const addFiles = useCallback((files) => {
    const valid = files.filter((f) => {
      if (f.size > MAX_FILE_SIZE) {
        alert(`${f.name} exceeds 10MB limit`);
        return false;
      }
      return true;
    });
    setPendingFiles((prev) => {
      const combined = [...prev, ...valid];
      if (combined.length > MAX_FILES) {
        alert(`Maximum ${MAX_FILES} files allowed`);
        return combined.slice(0, MAX_FILES);
      }
      return combined;
    });
  }, []);

  const handleFilesSelected = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      addFiles(files);
    },
    [addFiles]
  );

  const removePendingFile = useCallback((index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Drag and drop handlers (AI tab only)
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer?.types?.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length) addFiles(files);
    },
    [addFiles]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if ((!input.trim() && !pendingFiles.length) || isStreaming) return;
    sendMessage(input, pendingFiles);
    setInput('');
    setPendingFiles([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop on mobile */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 lg:bg-transparent z-[55]"
            onClick={close}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 z-[60] w-full sm:w-[400px] bg-base-100 border-l border-base-300 flex flex-col shadow-xl"
            onDragEnter={activeTab === 'ai' ? handleDragEnter : undefined}
            onDragLeave={activeTab === 'ai' ? handleDragLeave : undefined}
            onDragOver={activeTab === 'ai' ? handleDragOver : undefined}
            onDrop={activeTab === 'ai' ? handleDrop : undefined}
          >
            {/* Drag overlay (AI tab only) */}
            {isDragging && activeTab === 'ai' && (
              <div className="absolute inset-0 z-[60] bg-primary/10 border-2 border-dashed border-primary rounded-lg flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-primary mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm font-medium text-primary">Drop files here</p>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-3 pb-0">
              <h3 className="font-semibold text-sm">
                {activeTab === 'ai' ? 'Swan' : activeTab === 'email' ? 'Email' : 'Messages'}
              </h3>
              <div className="flex items-center gap-1">
                {activeTab === 'ai' && messages.length > 0 && (
                  <button
                    onClick={clearMessages}
                    className="btn btn-ghost btn-sm btn-square"
                    title="Clear chat"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
                <CloseButton onClick={close} />
              </div>
            </div>

            {/* Tab Bar */}
            <div className="flex border-b border-base-300 px-4">
              <button
                onClick={() => setActiveTab('ai')}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'ai'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-base-content/50 hover:text-base-content/70'
                }`}
              >
                {/* Sparkle icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Swan
              </button>
              <button
                onClick={() => setActiveTab('people')}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'people'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-base-content/50 hover:text-base-content/70'
                }`}
              >
                {/* Chat icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
                People
                {conversationUnreadCount > 0 && (
                  <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-error text-error-content text-[10px] font-bold rounded-full px-1">
                    {conversationUnreadCount > 99 ? '99+' : conversationUnreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('email')}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'email'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-base-content/50 hover:text-base-content/70'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'ai' ? (
              <>
                {/* AI Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.length === 0 && (
                    <div className="text-center text-base-content/40 mt-12 space-y-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-base-content/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                      <p className="text-sm font-medium">Ask me anything about your learners</p>
                      <div className="space-y-1.5">
                        {[
                          'Who is in class 7C?',
                          'Show discipline history for Sarah',
                          'Give John a merit for helping others',
                        ].map((suggestion) => (
                          <button
                            key={suggestion}
                            onClick={() => {
                              setInput(suggestion);
                              inputRef.current?.focus();
                            }}
                            className="block mx-auto text-xs text-primary/70 hover:text-primary cursor-pointer transition-colors"
                          >
                            &ldquo;{suggestion}&rdquo;
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {messages.map((msg) => (
                    <ChatMessage key={msg.id} message={msg} />
                  ))}

                  {isStreaming && (
                    <div className="flex items-center gap-2 text-base-content/40 text-sm">
                      <span className="loading loading-dots loading-xs"></span>
                      <span>Thinking...</span>
                    </div>
                  )}

                  {error && (
                    <div className="alert alert-error alert-sm text-sm break-words overflow-hidden">
                      Something went wrong. Please try again.
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* AI Input */}
                <form onSubmit={handleSubmit} className="p-3">
                  {/* Hidden file inputs */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    multiple
                    className="hidden"
                    onChange={handleFilesSelected}
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept={IMAGE_UPLOAD_ACCEPT}
                    capture="environment"
                    className="hidden"
                    onChange={handleFilesSelected}
                  />

                  {/* Unified input container */}
                  <div className="input input-bordered w-full flex flex-col h-auto min-h-[52px] py-2 px-3 hover:border-base-content/30 transition-colors">
                    {/* Attachment preview strip */}
                    {pendingFiles.length > 0 && (
                      <div className="flex gap-2 flex-wrap pb-2">
                        {pendingFiles.map((file, i) => (
                          <AttachmentPreview
                            key={`${file.name}-${i}`}
                            file={file}
                            onRemove={() => removePendingFile(i)}
                          />
                        ))}
                      </div>
                    )}

                    {/* Input row: + button, textarea, send button */}
                    <div className="flex items-end gap-1.5 w-full">
                      {/* + button with dropdown menu */}
                      <div className="relative shrink-0 self-end" ref={attachMenuRef}>
                        <button
                          type="button"
                          onClick={() => setShowAttachMenu((v) => !v)}
                          className={`p-1 rounded-md transition-colors ${showAttachMenu ? 'bg-base-200' : 'hover:bg-base-200'} ${isStreaming || pendingFiles.length >= MAX_FILES ? 'opacity-30 pointer-events-none' : 'text-base-content/50 hover:text-base-content/80'}`}
                          title="Add content"
                          disabled={isStreaming || pendingFiles.length >= MAX_FILES}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>

                        {/* Dropdown menu */}
                        <AnimatePresence>
                          {showAttachMenu && (
                            <motion.div
                              initial={{ opacity: 0, y: 8, scale: 0.95 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              exit={{ opacity: 0, y: 8, scale: 0.95 }}
                              transition={{ duration: 0.15 }}
                              className="absolute bottom-full left-0 mb-2 w-52 bg-base-100 rounded-xl shadow-lg border border-base-300 overflow-hidden z-10"
                            >
                              <button
                                type="button"
                                className="flex items-center gap-3 w-full px-4 py-3 text-sm hover:bg-base-200 transition-colors text-left"
                                onClick={() => {
                                  setShowAttachMenu(false);
                                  fileInputRef.current?.click();
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-base-content/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                Add files or photos
                              </button>
                              <button
                                type="button"
                                className="flex items-center gap-3 w-full px-4 py-3 text-sm hover:bg-base-200 transition-colors text-left"
                                onClick={() => {
                                  setShowAttachMenu(false);
                                  cameraInputRef.current?.click();
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-base-content/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Take photo
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Textarea */}
                      <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask about your learners..."
                        className="flex-1 bg-transparent border-none outline-none resize-none text-sm leading-snug min-h-[28px] max-h-[120px] py-1 placeholder:text-base-content/40"
                        rows={1}
                        disabled={isStreaming}
                      />

                      {/* Send / Stop button */}
                      {isStreaming ? (
                        <button
                          type="button"
                          onClick={cancel}
                          className="shrink-0 self-end p-1 rounded-md text-base-content/50 hover:text-base-content/80 hover:bg-base-200 transition-colors"
                          title="Stop"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="6" width="12" height="12" rx="2" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          type="submit"
                          disabled={!input.trim() && !pendingFiles.length}
                          className="shrink-0 self-end p-1.5 rounded-lg bg-primary text-primary-content disabled:opacity-30 disabled:cursor-not-allowed hover:brightness-110 transition-all"
                          title="Send"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </>
            ) : activeTab === 'email' ? (
              /* Email Tab */
              <EmailPanel />
            ) : (
              /* People Tab */
              <ConversationsPanelContent />
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/** Pending file preview (above textarea) */
function AttachmentPreview({ file, onRemove }) {
  const isImage = isSupportedImageFile(file);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!isImage) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  return (
    <div className="relative group">
      {isImage && preview ? (
        <img src={preview} alt={file.name} className="h-14 w-14 rounded-lg object-cover border border-base-300" />
      ) : (
        <div className="h-14 px-3 rounded-lg border border-base-300 bg-base-200 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-base-content/50 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-xs truncate max-w-[80px]">{file.name}</span>
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute -top-1.5 -right-1.5 btn btn-circle btn-xs btn-error opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/** Inline attachment display in sent message bubbles */
function MessageAttachments({ attachments }) {
  const [lightboxUrl, setLightboxUrl] = useState(null);

  if (!attachments?.length) return null;

  return (
    <>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {attachments.map((att, i) => {
          const isImage = att.mimeType?.startsWith('image/');
          if (isImage) {
            return (
              <img
                key={i}
                src={att.url}
                alt={att.name}
                className="h-20 w-20 rounded object-cover cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setLightboxUrl(att.url)}
              />
            );
          }
          return (
            <a
              key={i}
              href={att.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-black/10 text-xs hover:bg-black/20 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="truncate max-w-[100px]">{att.name}</span>
            </a>
          );
        })}
      </div>

      {/* Lightbox for image viewing */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <img src={lightboxUrl} alt="Full size" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </>
  );
}

/** Individual chat message bubble */
function ChatMessage({ message }) {
  const isUser = message.role === 'user';
  const hasBubbleContent = Boolean(
    message.content || (isUser && message.attachments?.length)
  );

  return (
    <div className={`chat ${isUser ? 'chat-end' : 'chat-start'}`}>
      {hasBubbleContent && (
        <div
          className={`chat-bubble ${
            isUser ? 'chat-bubble-primary' : ''
          } text-sm ${isUser ? 'whitespace-pre-wrap' : ''} break-words`}
        >
          {isUser ? message.content : <MarkdownText text={message.content} />}
          {isUser && <MessageAttachments attachments={message.attachments} />}
        </div>
      )}
      {/* Tool results + draft review + resolved students in chat-footer slot */}
      {!isUser && (message.toolResults?.length > 0 || message.resolvedStudents?.length > 0 || message.draftBatchId) && (
        <div className="chat-footer opacity-100">
          {message.toolResults?.length > 0 && (
            <div className="mt-1">
              {message.toolResults.map((tr, i) => (
                <ToolResultCard key={i} toolName={tr.toolName} result={tr.result} />
              ))}
            </div>
          )}
          {message.draftBatchId && (
            <DraftReviewPanel batchId={message.draftBatchId} />
          )}
          {message.resolvedStudents?.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {message.resolvedStudents.map((s) => (
                <span
                  key={s.id}
                  className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-base-200 text-base-content/50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  {s.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
